import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { Alert, Button, Card, Skeleton, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";
import ForecastDropdown from "./ForecastDropdown";
import ForecastPeriodDropdown from "./ForecastPeriodDropdown";
import axiosInstance from "../AxiosInstance";
import { generateMonthLabels } from "../Utilities/monthLabelUtils";
import CustomerOrderingDashboardPanel from "./CustomerOrderingDashboardPanel";

interface Forecast {
    forecastId: number;
    name: string;
    startPeriod: number;
    startDate: string;
    forecastPeriods: ForecastPeriod[];
}

interface ForecastPeriod {
    forecastPeriodId: number;
    forecastId: number;
    name: string;
    isLocked: boolean;
    periodIndex: number;
    hasForcastWeekElapsed: boolean;
}

interface ChartSeries {
    label: string;
    data: number[];
}



interface TopCustomerData {
    customerId: number;
    customerName: string;
    value: number;
    units: number;
}

interface TopSkuData {
    productId: number;
    skuDescription: string;
    value: number;
    units: number;
}

interface DashboardChartData {
    startMonth: number;
    fiscalCalendarRuleSet?: number;
    mainChart: ChartSeries[];
    topCustomers: TopCustomerData[];
    topSkus: TopSkuData[];
}

interface DatasetItem {
    label: string;
    [key: string]: number | string;
}

interface YtdPieLegendItem {
    id: number;
    label: string;
    value: number;
    units: number;
}

interface YtdPieWidgetProps {
    title: string;
    loading: boolean;
    items: YtdPieLegendItem[];
    emptyMessage: string;
    colors: string[];
    backgroundColor: string;
    formatCurrency: (value: number) => string;
    formatUnits: (value: number) => string;
}

const YtdPieWidget: React.FC<YtdPieWidgetProps> = ({
    title,
    loading,
    items,
    emptyMessage,
    colors,
    backgroundColor,
    formatCurrency,
    formatUnits,
}) => (
    <Card
        variant="outlined"
        sx={{
            borderRadius: 2,
            padding: "1rem",
            height: "100%",
            border: "none",
            backgroundColor,
            containerType: "inline-size",
        }}
    >
        <Typography variant="h6" align="left" gutterBottom>
            {title}
        </Typography>
        {loading ? (
            <Skeleton variant="rectangular" width="100%" height={300} />
        ) : items.length > 0 ? (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { "@": "column", "@560": "row" },
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    minHeight: 300,
                    height: "auto",
                }}
            >
                <Box sx={{ flex: "0 0 auto", display: "flex", justifyContent: "center" }}>
                    <PieChart
                        width={180}
                        height={180}
                        series={[
                            {
                                data: items.map((item, index) => ({
                                    id: item.id,
                                    value: item.value,
                                    label: item.label,
                                    color: colors[index % colors.length],
                                })),
                                highlightScope: { faded: "global", highlighted: "item" },
                                faded: { innerRadius: 30, additionalRadius: -30, color: "gray" },
                                cx: 90,
                                cy: 90,
                                innerRadius: 0,
                                outerRadius: 72,
                            },
                        ]}
                        margin={{ left: 0, right: 0, top: 0, bottom: 0 }}
                        slotProps={{
                            legend: { hidden: true },
                        }}
                    />
                </Box>
                <Box sx={{ flex: "1 1 200px", minWidth: 0, width: { "@": "100%", "@560": "auto" } }}>
                    <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                        {items.map((item, index) => (
                            <Box
                                component="li"
                                key={item.id}
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                    mb: 1,
                                    fontSize: "0.875rem",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: "50%",
                                        backgroundColor: colors[index % colors.length],
                                        flexShrink: 0,
                                        mt: "4px",
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        flex: "1 1 auto",
                                        minWidth: 0,
                                        overflowWrap: "anywhere",
                                        pr: 1,
                                    }}
                                >
                                    {item.label}
                                </Typography>
                                <Box sx={{ textAlign: "right", flexShrink: 0, whiteSpace: "nowrap" }}>
                                    <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                                        {formatCurrency(item.value)}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {formatUnits(item.units)}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        ) : (
            <Typography variant="body2" color="textSecondary">
                {emptyMessage}
            </Typography>
        )}
    </Card>
);

const Home: React.FC = () => {
    const theme = useTheme();
    const [mfaGraceBanner, setMfaGraceBanner] = useState<{ until: string } | null>(null);
    const [loadingForecasts, setLoadingForecasts] = useState<boolean>(true);
    const [forecasts, setForecasts] = useState<Forecast[]>([]);
    const [selectedForecastId, setSelectedForecastId] = useState<number | null>(null);
    const [selectedForecastPeriodId, setSelectedForecastPeriodId] = useState<number | null>(null);
    const [mainChartData, setMainChartData] = useState<ChartSeries[]>([]);
    const [dataset, setDataset] = useState<DatasetItem[]>([]);
    const [loadingChart, setLoadingChart] = useState<boolean>(false);
    const [topCustomersData, setTopCustomersData] = useState<TopCustomerData[]>([]);
    const [topSkusData, setTopSkusData] = useState<TopSkuData[]>([]);
    const [fiscalStartMonth, setFiscalStartMonth] = useState<number>(1);

    useEffect(() => {
        let cancelled = false;

        const loadMfaStatus = async (): Promise<void> => {
            try {
                const { data } = await axiosInstance.get<{
                    enabled: boolean;
                    graceEndsAtUtc?: string | null;
                }>("/mfa/status");
                if (cancelled) {
                    return;
                }
                if (!data.enabled && data.graceEndsAtUtc) {
                    const end = new Date(data.graceEndsAtUtc).getTime();
                    if (end > Date.now()) {
                        setMfaGraceBanner({ until: new Date(data.graceEndsAtUtc).toLocaleString() });
                        return;
                    }
                }
                setMfaGraceBanner(null);
            } catch {
                if (!cancelled) {
                    setMfaGraceBanner(null);
                }
            }
        };

        void loadMfaStatus();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const storedForecast = JSON.parse(localStorage.getItem("selectedForecast") || "{}");
        const storedForecastPeriodId = localStorage.getItem("selectedForecastPeriodId");

        if (storedForecast?.forecastId) {
            setSelectedForecastId(storedForecast.forecastId);
        }

        if (storedForecastPeriodId && storedForecastPeriodId !== "null") {
            setSelectedForecastPeriodId(parseInt(storedForecastPeriodId, 10));
        } else {
            setSelectedForecastPeriodId(null);
        }

        fetchForecastsAndPeriods();
    }, []);

    const fetchForecastsAndPeriods = async (): Promise<void> => {
        try {
            setLoadingForecasts(true);
            const response = await axiosInstance.get<Forecast[]>("/forecasts");
            setForecasts(response.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingForecasts(false);
        }
    };

    const handleForecastSelected = (id: number): void => {
        localStorage.setItem("selectedForecast", JSON.stringify({ forecastId: id }));
        setSelectedForecastId(id);
        setSelectedForecastPeriodId(null);
    };

    const handleForecastPeriodSelected = (forecastPeriodId: number | null): void => {
        if (forecastPeriodId) {
            localStorage.setItem("selectedForecastPeriodId", forecastPeriodId.toString());
            setSelectedForecastPeriodId(forecastPeriodId);
        } else {
            localStorage.removeItem("selectedForecastPeriodId");
            setSelectedForecastPeriodId(null);
        }
    };

    const fetchDashboardData = async (): Promise<void> => {
        if (!selectedForecastId || !selectedForecastPeriodId) return;

        try {
            setLoadingChart(true);
            const response = await axiosInstance.get<DashboardChartData>("/dashboard", {
                params: {
                    forecastId: selectedForecastId,
                    forecastPeriodId: selectedForecastPeriodId,
                },
            });

            const data = response.data;
            const startMonth = data.startMonth;

            const months = generateMonthLabels(startMonth, data.fiscalCalendarRuleSet);

            const newDataset: DatasetItem[] = months.map((month, index) => ({
                label: month,
                ...data.mainChart?.reduce((acc: Record<string, number>, series: ChartSeries) => {
                    acc[series.label] = series.data[index] || 0;
                    return acc;
                }, {} as Record<string, number>),
            }));

            setDataset(newDataset);
            setFiscalStartMonth(startMonth || 1);
            setMainChartData(data.mainChart || []);
            setTopCustomersData(data.topCustomers || []);
            setTopSkusData(data.topSkus || []);
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
            setMainChartData([]);
            setDataset([]);
            setFiscalStartMonth(1);
            setTopCustomersData([]);
            setTopSkusData([]);
        } finally {
            setLoadingChart(false);
        }
    };

    useEffect(() => {
        if (selectedForecastId && selectedForecastPeriodId) {
            fetchDashboardData();
        }
    }, [selectedForecastId, selectedForecastPeriodId]);

    const selectedForecast = forecasts.find((f) => f.forecastId === selectedForecastId);

    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const formatUnits = (value: number): string => {
        return new Intl.NumberFormat('en-GB', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const pieChartColors = ['#1a237e', '#3f51b5', '#5c6bc0', '#90caf9', '#bbdefb'];
    const chartPanelBackground = theme.palette.mode === "light" ? "#e0edff40" : theme.palette.action.hover;

    const getSeriesByLabel = (label: string): number[] => {
        return mainChartData.find((series) => series.label === label)?.data || [];
    };

    const nonActualSeries = mainChartData.filter((series) => series.label !== "Actuals");
    const budgetSeries = nonActualSeries[0];
    const forecastSeries = nonActualSeries[1] || nonActualSeries[0];

    const budgetLabel = budgetSeries?.label || "Budget";
    const forecastLabel = forecastSeries?.label || "Forecast";
    const actualsLabel = "Actuals";

    const budgetData = budgetSeries?.data || [];
    const forecastData = forecastSeries?.data || [];
    const actualsData = getSeriesByLabel(actualsLabel);

    const fullYearBudget = budgetData.reduce((sum, value) => sum + value, 0);
    const fullYearForecast = forecastData.reduce((sum, value) => sum + value, 0);

    const now = new Date();
    const fiscalCurrentMonthIndex = ((now.getMonth() + 1) - fiscalStartMonth + 12) % 12;
    const ytdMonthCount = Math.min(fiscalCurrentMonthIndex + 1, Math.max(actualsData.length, forecastData.length, 12));

    const ytdActuals = actualsData.slice(0, ytdMonthCount).reduce((sum, value) => sum + value, 0);
    const ytdForecast = forecastData.slice(0, ytdMonthCount).reduce((sum, value) => sum + value, 0);

    const fullYearVariance = fullYearForecast - fullYearBudget;
    const fullYearVariancePct = fullYearBudget === 0 ? 0 : (fullYearVariance / fullYearBudget) * 100;
    const ytdAttainmentPct = ytdForecast === 0 ? 0 : (ytdActuals / ytdForecast) * 100;

    const monthlyVarianceDataset = dataset.map((month, index) => {
        const variance = (forecastData[index] || 0) - (budgetData[index] || 0);

        return {
            label: month.label,
            variance,
            positiveVariance: Math.max(variance, 0),
            negativeVariance: Math.min(variance, 0),
        };
    });

    const maxAbsVariance = Math.max(
        1,
        ...monthlyVarianceDataset.map((month) => Math.abs(month.variance)),
    );

    return (
        <Box className="component-container">
            {mfaGraceBanner ? (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" component={RouterLink} to="/settings/profile">
                            Set up MFA
                        </Button>
                    }
                >
                    Secure your account: enable two-factor authentication before {mfaGraceBanner.until}.
                </Alert>
            ) : null}
            <Card variant="outlined" sx={{ borderRadius: 2, padding: "1rem", mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, pb: 2 }}>
                    {loadingForecasts ? (
                        <Skeleton variant="rectangular" width={200} height={40} />
                    ) : (
                        <ForecastDropdown
                            forecasts={forecasts}
                            selectedForecastId={selectedForecastId}
                            onForecastSelected={handleForecastSelected}
                        />
                    )}
                    {loadingForecasts ? (
                        <Skeleton variant="rectangular" width={200} height={40} />
                    ) : (
                        <ForecastPeriodDropdown
                            forecastPeriods={selectedForecast?.forecastPeriods || []}
                            selectedForecastPeriodId={selectedForecastPeriodId}
                            onForecastPeriodSelected={handleForecastPeriodSelected}
                        />
                    )}
                </Box>

                {selectedForecastId && selectedForecastPeriodId && (
                    <>
                        <Card
                            variant="outlined"
                            sx={{ borderRadius: 2, padding: "1rem", mb: 3, border: "none", backgroundColor: chartPanelBackground }}
                        >
                        <Typography variant="h6" align="left" gutterBottom>
                            Gross Revenue (£)
                        </Typography>
                        {loadingChart ? (
                            <Skeleton variant="rectangular" width="100%" height={300} />
                        ) : (
                            <BarChart
                                height={300}
                                dataset={dataset}
                                xAxis={[
                                    {
                                        dataKey: "label",
                                        label: "Months",
                                        scaleType: "band",
                                    },
                                ]}
                                series={mainChartData?.map((s) => ({
                                    dataKey: s.label,
                                    label: s.label,
                                })) || []}
                                margin={{ left: 80, right: 20, top: 20, bottom: 20 }}
                            />
                        )}
                    </Card>

                    {/* TODO These dashboard were created by Codex, removing them for now */}
                    {/*<Grid container spacing={3} sx={{ mb: 3 }}>*/}
                    {/*    <Grid size={{ xs: 12, md: 4 }}>*/}
                    {/*        <Card variant="outlined" sx={{ borderRadius: 2, padding: "1rem", minHeight: 116, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>*/}
                    {/*            <Typography variant="overline" color="text.secondary">*/}
                    {/*                YTD Actual Revenue*/}
                    {/*            </Typography>*/}
                    {/*            {loadingChart ? (*/}
                    {/*                <>*/}
                    {/*                    <Skeleton variant="text" width="60%" height={42} />*/}
                    {/*                    <Skeleton variant="text" width="45%" height={20} />*/}
                    {/*                </>*/}
                    {/*            ) : (*/}
                    {/*                <>*/}
                    {/*                    <Typography variant="h5">{formatCurrency(ytdActuals)}</Typography>*/}
                    {/*                    <Typography variant="body2" color="text.secondary" sx={{ visibility: "hidden" }} aria-hidden>*/}
                    {/*                        {"\u00A0"}*/}
                    {/*                    </Typography>*/}
                    {/*                </>*/}
                    {/*            )}*/}
                    {/*        </Card>*/}
                    {/*    </Grid>*/}
                    {/*    <Grid size={{ xs: 12, md: 4 }}>*/}
                    {/*        <Card variant="outlined" sx={{ borderRadius: 2, padding: "1rem", minHeight: 116, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>*/}
                    {/*            <Typography variant="overline" color="text.secondary">*/}
                    {/*                Actual vs Forecast Attainment*/}
                    {/*            </Typography>*/}
                    {/*            {loadingChart ? (*/}
                    {/*                <>*/}
                    {/*                    <Skeleton variant="text" width="55%" height={42} />*/}
                    {/*                    <Skeleton variant="text" width="70%" height={20} />*/}
                    {/*                </>*/}
                    {/*            ) : (*/}
                    {/*                <>*/}
                    {/*                    <Typography variant="h5">{ytdAttainmentPct.toFixed(1)}%</Typography>*/}
                    {/*                    <Typography variant="body2" color="text.secondary">*/}
                    {/*                        vs {formatCurrency(ytdForecast)} YTD forecast*/}
                    {/*                    </Typography>*/}
                    {/*                </>*/}
                    {/*            )}*/}
                    {/*        </Card>*/}
                    {/*    </Grid>*/}
                    {/*    <Grid size={{ xs: 12, md: 4 }}>*/}
                    {/*        <Card variant="outlined" sx={{ borderRadius: 2, padding: "1rem", minHeight: 116, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>*/}
                    {/*            <Typography variant="overline" color="text.secondary">*/}
                    {/*                Full-Year Forecast vs Budget*/}
                    {/*            </Typography>*/}
                    {/*            {loadingChart ? (*/}
                    {/*                <>*/}
                    {/*                    <Skeleton variant="text" width="50%" height={42} />*/}
                    {/*                    <Skeleton variant="text" width="30%" height={20} />*/}
                    {/*                </>*/}
                    {/*            ) : (*/}
                    {/*                <>*/}
                    {/*                    <Typography variant="h5">{formatCurrency(fullYearVariance)}</Typography>*/}
                    {/*                    <Typography variant="body2" color={fullYearVariance >= 0 ? "success.main" : "error.main"}>*/}
                    {/*                        {fullYearVariancePct.toFixed(1)}%*/}
                    {/*                    </Typography>*/}
                    {/*                </>*/}
                    {/*            )}*/}
                    {/*        </Card>*/}
                    {/*    </Grid>*/}
                    {/*</Grid>*/}
                    
                    {/*<Card variant="outlined" sx={{ borderRadius: 2, padding: "1rem", mb: 3 }}>*/}
                    {/*    <Typography variant="h6" align="left" gutterBottom>*/}
                    {/*        Monthly Forecast Variance (£)*/}
                    {/*    </Typography>*/}
                    {/*    {loadingChart ? (*/}
                    {/*        <Skeleton variant="rectangular" width="100%" height={260} />*/}
                    {/*    ) : (*/}
                    {/*        <BarChart*/}
                    {/*            height={260}*/}
                    {/*            dataset={monthlyVarianceDataset}*/}
                    {/*            xAxis={[{ dataKey: "label", scaleType: "band", label: "Months" }]}*/}
                    {/*            yAxis={[{*/}
                    {/*                min: -maxAbsVariance,*/}
                    {/*                max: maxAbsVariance,*/}
                    {/*                valueFormatter: (value) => formatCurrency(value as number),*/}
                    {/*            }]}*/}
                    {/*            series={[*/}
                    {/*                { dataKey: "positiveVariance", label: "Positive variance", color: "#26a69a", stack: "variance" },*/}
                    {/*                { dataKey: "negativeVariance", label: "Negative variance", color: "#ef5350", stack: "variance" },*/}
                    {/*            ]}*/}
                    {/*            margin={{ left: 80, right: 20, top: 20, bottom: 20 }}*/}
                    {/*            slotProps={{*/}
                    {/*                legend: { hidden: true },*/}
                    {/*            }}*/}
                    {/*        />*/}
                    {/*    )}*/}
                    {/*</Card>*/}

                        <Grid container spacing={3} sx={{ mb: 1 }}>
                            <Grid size={{ xs: 12, xl: 6 }}>
                                <YtdPieWidget
                                    title="YTD Top Customers"
                                    loading={loadingChart}
                                    items={topCustomersData.map((customer) => ({
                                        id: customer.customerId,
                                        label: customer.customerName,
                                        value: customer.value,
                                        units: customer.units,
                                    }))}
                                    emptyMessage="No customer data available"
                                    colors={pieChartColors}
                                    backgroundColor={chartPanelBackground}
                                    formatCurrency={formatCurrency}
                                    formatUnits={formatUnits}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, xl: 6 }}>
                                <YtdPieWidget
                                    title="YTD Top Products"
                                    loading={loadingChart}
                                    items={topSkusData.map((sku) => ({
                                        id: sku.productId,
                                        label: sku.skuDescription,
                                        value: sku.value,
                                        units: sku.units,
                                    }))}
                                    emptyMessage="No SKU data available"
                                    colors={pieChartColors}
                                    backgroundColor={chartPanelBackground}
                                    formatCurrency={formatCurrency}
                                    formatUnits={formatUnits}
                                />
                            </Grid>
                        </Grid>
                    </>
                )}
            </Card>
            <CustomerOrderingDashboardPanel />
        </Box>
    );
};

export default Home; 
